import React, { useState, useEffect } from 'react';
import HeroSlider from '../components/HeroSlider';
import Footer from '../components/Footer';
import { getFAQs } from '../supabase';
import {
  ShieldCheck, GraduationCap, Clock3, BadgeDollarSign, Lock, RefreshCw,
  CreditCard, Presentation,
  MonitorCheck, ClipboardList, SpellCheck, FileText, Star, ChevronDown,
  MessageCircle, UploadCloud, PlayCircle, SearchCheck, CheckCircle2,
  BarChart3, MessagesSquare, ArrowRight, Zap,
  PencilLine, BookOpen, FlaskConical,
} from 'lucide-react';

// ── DATA (unchanged) ──────────────────────────────────────────────────────────
const services = [
  { icon:PencilLine,   color:'#0F52BA', bg:'rgba(15,82,186,0.08)',  title:'Essay Writing',    sub:'All types & levels' },
  { icon:BookOpen,     color:'#FF7A00', bg:'rgba(255,122,0,0.08)',   title:'Assignments',      sub:'Any subject & format' },
  { icon:GraduationCap,color:'#7C3AED', bg:'rgba(124,58,237,0.08)', title:'Dissertation',     sub:'Full thesis support' },
  { icon:FlaskConical, color:'#0891B2', bg:'rgba(8,145,178,0.08)',   title:'Research Papers',  sub:'Peer-review quality' },
  { icon:Presentation, color:'#16a34a', bg:'rgba(22,163,74,0.08)',   title:'Presentations',    sub:'PPT & Prezi design' },
  { icon:MonitorCheck, color:'#BE185D', bg:'rgba(190,24,93,0.08)',   title:'Online Classes',   sub:'Full semester support' },
  { icon:ClipboardList,color:'#EA580C', bg:'rgba(234,88,12,0.08)',   title:'Case Studies',     sub:'Deep analysis' },
  { icon:SpellCheck,   color:'#475569', bg:'rgba(71,85,105,0.08)',   title:'Proofreading',     sub:'Edit & polish' },
];

const stats = [
  { num:'12,000+', label:'Assignments Completed' },
  { num:'6,000+',  label:'Happy Students' },
  { num:'150+',    label:'Subject Experts' },
  { num:'98%',     label:'Satisfaction Rate' },
];

const features = [
  { icon:ShieldCheck,    title:'100% Original Work',   desc:'Every assignment is written from scratch with full Turnitin-proof plagiarism-free guarantees.' },
  { icon:GraduationCap,  title:'Qualified Experts',    desc:"Master's and PhD-level writers with verified credentials across 150+ academic subjects." },
  { icon:Clock3,         title:'On-Time Delivery',     desc:'We guarantee delivery before your deadline — even for urgent 3-hour orders.' },
  { icon:BadgeDollarSign,title:'Affordable Pricing',   desc:'Competitive rates tailored for students. Transparent pricing with zero hidden charges.' },
  { icon:Lock,           title:'100% Confidential',    desc:'Your identity and order details are fully protected with enterprise-grade encryption.' },
  { icon:RefreshCw,      title:'Unlimited Revisions',  desc:"Not satisfied? Request as many revisions as you need until you're completely happy — free." },
];

const steps = [
  { icon:UploadCloud,  num:1, title:'Submit Requirements', desc:'Fill in your details and requirements' },
  { icon:FileText,     num:2, title:'Receive Quote',       desc:'Get a free quote within 30 minutes',  accent:true },
  { icon:CreditCard,   num:3, title:'Secure Payment',      desc:'Pay safely via card, PayPal or bank' },
  { icon:PlayCircle,   num:4, title:'Project Starts',      desc:'Your expert writer gets to work' },
  { icon:SearchCheck,  num:5, title:'Quality Review',      desc:'QA team checks everything' },
  { icon:CheckCircle2, num:6, title:'Delivered',           desc:'Before your deadline, guaranteed' },
];

const reviews = [
  { initials:'TM', bg:'#0F52BA', name:'T.M.', country:'Malaysia · Master\'s', service:'Thesis Structure',   text:'Support with my master\'s thesis was organised and easy to follow. The chapters were aligned with my research objectives, and recommendations helped me respond to my supervisor\'s comments more confidently.' },
  { initials:'AM', bg:'#DC2626', name:'A.M.', country:'Philippines · Nursing', service:'Nursing Assignment', text:'The nursing paper followed the instructions closely and used relevant academic evidence. The discussion connected theory with clinical practice and was presented in a clear, professional style.' },
  { initials:'JK', bg:'#FF7A00', name:'J.K.', country:'Thailand · MBA',        service:'Business Case Study',text:'The case study went beyond describing the company. It applied required business models, evaluated challenges, and provided practical recommendations supported by strong academic evidence.' },
];

const subjects = ['Nursing','Business','Law','Computer Science','Psychology','Engineering','Accounting','Finance','Marketing','Economics','Education','Medicine','Data Science','Architecture','Public Health','HRM','Sociology','Mathematics','Chemistry','Biology'];

const sampleItems = [
  { label:'PhD Theses',             color:'#7C3AED', Icon:GraduationCap },
  { label:"Master's Dissertations", color:'#0F52BA', Icon:BookOpen },
  { label:'Undergraduate Projects', color:'#16a34a', Icon:ClipboardList },
  { label:'Research Proposals',     color:'#0891B2', Icon:FlaskConical },
  { label:'Essays & Papers',        color:'#FF7A00', Icon:PencilLine },
  { label:'Case Studies',           color:'#BE185D', Icon:BarChart3 },
  { label:'Data Analysis',          color:'#6D28D9', Icon:MessagesSquare },
  { label:'Editing Samples',        color:'#475569', Icon:SpellCheck },
];

const DEFAULT_FAQS = [
  { q:'Is my order kept confidential?',         a:'Absolutely. We use end-to-end encryption and never share your personal information or order details with any third party. Your academic institution will never know you used our service.' },
  { q:"What if I'm not satisfied with the work?", a:'We offer unlimited free revisions until you are fully satisfied with the quality. In rare cases where we cannot meet your requirements, we offer a full refund.' },
  { q:'How do I track my assignment progress?', a:"After placing your order you'll receive a unique tracking ID. Visit our Track Order page and enter your ID to see real-time status updates." },
  { q:'What payment methods do you accept?',    a:'We accept Credit/Debit Card (Visa, Mastercard, Amex), PayPal, and Bank Transfer. All payments are fully secured and protected.' },
];

// ── FAQ ACCORDION ─────────────────────────────────────────────────────────────
function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{borderBottom:'1px solid #e8edf2',overflow:'hidden'}}>
      <button onClick={() => setOpen(o => !o)} style={{
        width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'20px 0',background:'none',border:'none',cursor:'pointer',
        fontFamily:'Sora,sans-serif',fontSize:'clamp(14px,1.5vw,16px)',fontWeight:600,
        color:'#0A1931',textAlign:'left',gap:12,
      }}>
        {q}
        <ChevronDown size={18} style={{flexShrink:0,transition:'transform 0.25s',transform:open?'rotate(-180deg)':'rotate(0)'}}/>
      </button>
      <div style={{
        maxHeight:open?400:0,overflow:'hidden',transition:'max-height 0.3s ease',
      }}>
        <p style={{fontSize:15,color:'#4b5563',lineHeight:1.75,margin:'0 0 20px',paddingRight:32}}>{a}</p>
      </div>
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function HomePage({ navigate }) {
  const [faqs, setFaqs] = useState(DEFAULT_FAQS);

  useEffect(() => {
    getFAQs()
      .then(({ data }) => {
        if (data?.length) {
          const active = data.filter(f => f.is_active !== false);
          if (active.length) setFaqs(active.map(f => ({ q:f.question, a:f.answer })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div style={{fontFamily:'Inter,sans-serif'}}>
      <HeroSlider navigate={navigate}/>

      {/* ── SERVICES STRIP — horizontal scroll on mobile ── */}
      <section style={{padding:'64px 0',background:'#fff',overflow:'hidden'}}>
        <div style={{padding:'0 5%',marginBottom:32,display:'flex',alignItems:'flex-end',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
          <div>
            <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:6}}>What We Offer</p>
            <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.5rem,3vw,2.2rem)',fontWeight:800,color:'#0A1931',margin:0}}>
              Academic Services Tailored to You
            </h2>
          </div>
          <button onClick={() => navigate('services')} style={{display:'flex',alignItems:'center',gap:6,background:'none',border:'2px solid #0A1931',borderRadius:8,padding:'8px 18px',fontFamily:'Sora,sans-serif',fontSize:13,fontWeight:700,color:'#0A1931',cursor:'pointer'}}>
            All Services <ArrowRight size={14}/>
          </button>
        </div>
        <div style={{display:'flex',gap:16,overflowX:'auto',padding:'0 5% 8px',scrollbarWidth:'none',WebkitOverflowScrolling:'touch'}}>
          {services.map((s,i) => (
            <div key={i} onClick={() => navigate('services')} style={{
              minWidth:160,flexShrink:0,background:'#fff',border:'1.5px solid #e8edf2',
              borderRadius:16,padding:'22px 18px',cursor:'pointer',transition:'all 0.2s',
              display:'flex',flexDirection:'column',gap:12,
            }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=s.color;e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow=`0 8px 24px ${s.color}18`}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8edf2';e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none'}}>
              <div style={{width:44,height:44,borderRadius:11,background:s.bg,color:s.color,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <s.icon size={22}/>
              </div>
              <div>
                <div style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:14,color:'#0A1931',marginBottom:3}}>{s.title}</div>
                <div style={{fontSize:12,color:'#6B7A99'}}>{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div style={{background:'#0A1931',padding:'48px 5%'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:0,maxWidth:960,margin:'0 auto'}}>
          {stats.map((s,i) => (
            <div key={i} style={{textAlign:'center',padding:'16px',borderRight:i<stats.length-1?'1px solid rgba(255,255,255,0.1)':'none'}}>
              <div style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(2rem,4vw,2.8rem)',fontWeight:800,color:'#FF7A00',lineHeight:1}}>{s.num}</div>
              <div style={{fontSize:13,color:'rgba(255,255,255,0.55)',marginTop:6,fontWeight:500}}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHY US — BENTO GRID ── */}
      <section style={{padding:'80px 5%',background:'#f8f9fc'}}>
        <div style={{marginBottom:48,maxWidth:560}}>
          <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>Why Students Trust Us</p>
          <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,3vw,2.4rem)',fontWeight:800,color:'#0A1931',margin:'0 0 16px',lineHeight:1.2}}>
            Why Choose Ark Expert Researchers?
          </h2>
          <p style={{fontSize:15,color:'#6B7A99',lineHeight:1.7,margin:0}}>
            Everything you need to succeed academically — quality, speed, and total confidentiality — guaranteed.
          </p>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:16,maxWidth:1100,margin:'0 auto'}}>
          {features.map((f,i) => (
            <div key={i} style={{
              background:'#fff',borderRadius:16,padding:'28px',
              border:'1px solid #e8edf2',display:'flex',gap:18,alignItems:'flex-start',
              transition:'box-shadow 0.2s',
            }}
              onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 20px rgba(15,82,186,0.1)'}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
              <div style={{width:46,height:46,borderRadius:12,background:'linear-gradient(135deg,#0F52BA12,#0F52BA22)',color:'#0F52BA',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <f.icon size={22}/>
              </div>
              <div>
                <div style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,color:'#0A1931',marginBottom:6}}>{f.title}</div>
                <p style={{fontSize:13.5,color:'#6B7A99',lineHeight:1.65,margin:0}}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FREE FEATURES — SPLIT LAYOUT ── */}
      <section style={{background:'linear-gradient(135deg,#0a1931 0%,#0F52BA 100%)',padding:'80px 5%'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:48,maxWidth:1100,margin:'0 auto',alignItems:'center'}}>
          <div>
            <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FFB347',marginBottom:12}}>Included With Every Order</p>
            <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,3vw,2.4rem)',fontWeight:800,color:'#fff',margin:'0 0 16px',lineHeight:1.2}}>
              What You Get — Absolutely Free
            </h2>
            <p style={{fontSize:15,color:'rgba(255,255,255,0.62)',lineHeight:1.7,margin:'0 0 28px'}}>
              Every client at Ark Expert Researchers gets these premium extras at no extra cost.
            </p>
            <button className="btn-primary" style={{background:'#FF7A00',border:'none'}} onClick={() => navigate('quote')}>
              <Zap size={16}/> Get Free Quote
            </button>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {[
              { Icon:BarChart3,     color:'#60a5fa', title:'Free SPSS Analysis',   desc:'Full statistical analysis included with qualifying research projects.' },
              { Icon:MessageCircle, color:'#4ade80', title:'Free Consultation',     desc:'Expert consultation before you place your order — no commitment.' },
              { Icon:BookOpen,      color:'#fb923c', title:'Free Referencing',      desc:'APA, Harvard, MLA, Chicago — any citation style formatted for free.' },
            ].map((f,i) => (
              <div key={i} style={{display:'flex',gap:16,alignItems:'flex-start',background:'rgba(255,255,255,0.06)',borderRadius:12,padding:'18px 20px',border:'1px solid rgba(255,255,255,0.1)'}}>
                <div style={{width:42,height:42,borderRadius:10,background:`${f.color}22`,color:f.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <f.Icon size={20}/>
                </div>
                <div>
                  <div style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:14,color:'#fff',marginBottom:4}}>
                    {f.title}
                    <span style={{marginLeft:8,fontSize:10,fontWeight:700,background:'rgba(255,179,71,0.2)',color:'#FFB347',padding:'2px 8px',borderRadius:10,verticalAlign:'middle'}}>FREE</span>
                  </div>
                  <p style={{fontSize:13,color:'rgba(255,255,255,0.58)',margin:0,lineHeight:1.6}}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — NUMBERED TIMELINE ── */}
      <section style={{padding:'80px 5%',background:'#fff'}}>
        <div style={{textAlign:'center',marginBottom:52}}>
          <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>How It Works</p>
          <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,3vw,2.4rem)',fontWeight:800,color:'#0A1931',margin:0}}>
            6 Simple Steps to Academic Success
          </h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:24,maxWidth:1060,margin:'0 auto',position:'relative'}}>
          {steps.map((s,i) => (
            <div key={i} style={{textAlign:'center',display:'flex',flexDirection:'column',alignItems:'center',gap:14}}>
              <div style={{
                width:56,height:56,borderRadius:'50%',
                background: s.accent ? '#FF7A00' : '#0A1931',
                color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                boxShadow: s.accent ? '0 0 0 6px rgba(255,122,0,0.15)' : 'none',
                position:'relative',
              }}>
                <s.icon size={22}/>
                <span style={{position:'absolute',top:-6,right:-6,width:20,height:20,borderRadius:'50%',background:s.accent?'#0A1931':'#FF7A00',color:'#fff',fontSize:10,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center'}}>{s.num}</span>
              </div>
              <div>
                <div style={{fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:14,color:'#0A1931',marginBottom:4}}>{s.title}</div>
                <p style={{fontSize:12.5,color:'#6B7A99',margin:0,lineHeight:1.6}}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── SUBJECTS ── */}
      <section style={{padding:'72px 5%',background:'#f8f9fc'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:48,maxWidth:1100,margin:'0 auto',alignItems:'center'}}>
          <div>
            <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>Academic Coverage</p>
            <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.5rem,3vw,2.2rem)',fontWeight:800,color:'#0A1931',margin:'0 0 16px'}}>30+ Subjects Covered</h2>
            <p style={{fontSize:15,color:'#6B7A99',lineHeight:1.7,margin:'0 0 28px'}}>
              From nursing to computer science, law to data analytics — our specialists cover every major discipline.
            </p>
            <button className="btn-primary" onClick={() => navigate('subjects')}>
              View All Subjects <ArrowRight size={14}/>
            </button>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
            {subjects.map(s => (
              <span key={s} onClick={() => navigate('subjects')} style={{
                padding:'7px 14px',borderRadius:20,background:'#fff',
                border:'1.5px solid #e8edf2',fontSize:13,fontWeight:600,
                color:'#374151',cursor:'pointer',transition:'all 0.18s',
              }}
                onMouseEnter={e=>{e.target.style.borderColor='#0F52BA';e.target.style.color='#0F52BA';e.target.style.background='#eff6ff'}}
                onMouseLeave={e=>{e.target.style.borderColor='#e8edf2';e.target.style.color='#374151';e.target.style.background='#fff'}}>
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section style={{padding:'80px 5%',background:'#0A1931'}}>
        <div style={{textAlign:'center',marginBottom:48}}>
          <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>Client Testimonials</p>
          <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,3vw,2.4rem)',fontWeight:800,color:'#fff',margin:0}}>
            What Our Clients Say
          </h2>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:20,maxWidth:1100,margin:'0 auto 36px'}}>
          {reviews.map((r,i) => (
            <div key={i} style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:16,padding:'28px',display:'flex',flexDirection:'column',gap:16}}>
              <div style={{color:'#FFB300',fontSize:16,letterSpacing:2}}>★★★★★</div>
              <p style={{fontSize:14,color:'rgba(255,255,255,0.75)',lineHeight:1.75,margin:0,flex:1}}>"{r.text}"</p>
              <div style={{display:'flex',alignItems:'center',gap:12,paddingTop:16,borderTop:'1px solid rgba(255,255,255,0.08)'}}>
                <div style={{width:40,height:40,borderRadius:'50%',background:r.bg,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:14,flexShrink:0}}>{r.initials}</div>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:'#fff'}}>{r.name}</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,0.45)'}}>{r.country}</div>
                </div>
                <span style={{marginLeft:'auto',fontSize:10,fontWeight:700,background:'rgba(255,122,0,0.15)',color:'#FF7A00',padding:'3px 9px',borderRadius:8,whiteSpace:'nowrap'}}>{r.service}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{textAlign:'center'}}>
          <button onClick={() => navigate('reviews')} style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:10,padding:'12px 24px',color:'#fff',fontFamily:'Sora,sans-serif',fontWeight:600,fontSize:14,cursor:'pointer',transition:'all 0.2s'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.14)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
            <Star size={15}/> View All Reviews
          </button>
        </div>
      </section>

      {/* ── SAMPLES GRID ── */}
      <section style={{padding:'80px 5%',background:'#fff'}}>
        <div style={{textAlign:'center',marginBottom:48}}>
          <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>Work Portfolio</p>
          <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,3vw,2.4rem)',fontWeight:800,color:'#0A1931',margin:'0 0 12px'}}>Browse Our Academic Samples</h2>
          <p style={{fontSize:15,color:'#6B7A99',maxWidth:520,margin:'0 auto'}}>See the quality before you order. Anonymised samples across PhD, Master's, undergraduate levels and all service types.</p>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:14,maxWidth:1000,margin:'0 auto 32px'}}>
          {sampleItems.map(({label,color,Icon}) => (
            <div key={label} onClick={() => navigate('samples')} style={{
              background:'#fff',border:'1.5px solid #e8edf2',borderRadius:14,padding:'20px 16px',
              textAlign:'center',cursor:'pointer',transition:'all 0.2s',display:'flex',flexDirection:'column',alignItems:'center',gap:12,
            }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow=`0 6px 20px ${color}18`}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#e8edf2';e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='none'}}>
              <div style={{width:48,height:48,borderRadius:12,background:`${color}15`,color,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Icon size={24}/>
              </div>
              <div style={{fontSize:13,fontWeight:700,color:'#0A1931',lineHeight:1.3}}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{textAlign:'center'}}>
          <button className="btn-primary" onClick={() => navigate('samples')}>Browse All Samples</button>
        </div>
      </section>

      {/* ── FAQ — TWO COLUMN ── */}
      <section style={{padding:'80px 5%',background:'#f8f9fc'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:48,maxWidth:1100,margin:'0 auto',alignItems:'start'}}>
          <div style={{position:'sticky',top:80}}>
            <p style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:'#FF7A00',marginBottom:8}}>Got Questions?</p>
            <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.5rem,3vw,2.2rem)',fontWeight:800,color:'#0A1931',margin:'0 0 16px'}}>
              Frequently Asked Questions
            </h2>
            <p style={{fontSize:15,color:'#6B7A99',lineHeight:1.7,margin:'0 0 28px'}}>
              Still have questions? Our team is available 24/7 to help.
            </p>
            <button onClick={() => navigate('contact')} style={{display:'inline-flex',alignItems:'center',gap:8,background:'#0A1931',border:'none',borderRadius:10,padding:'12px 22px',color:'#fff',fontFamily:'Sora,sans-serif',fontWeight:600,fontSize:14,cursor:'pointer'}}>
              <MessageCircle size={15}/> Ask Us Anything
            </button>
          </div>
          <div style={{background:'#fff',borderRadius:16,padding:'8px 28px',border:'1px solid #e8edf2'}}>
            {faqs.map((f,i) => <FAQ key={i} q={f.q} a={f.a}/>)}
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section style={{padding:'80px 5%',background:'linear-gradient(135deg,#FF7A00 0%,#e85d00 100%)',textAlign:'center'}}>
        <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.6rem,4vw,2.8rem)',fontWeight:800,color:'#fff',margin:'0 0 12px',lineHeight:1.2}}>
          Ready to Ace Your Assignment?
        </h2>
        <p style={{fontSize:16,color:'rgba(255,255,255,0.82)',margin:'0 0 36px'}}>
          Join 6,000+ students who've trusted Ark Expert Researchers with their academic success
        </p>
        <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
          <button onClick={() => navigate('quote')} style={{display:'inline-flex',alignItems:'center',gap:8,background:'#fff',border:'none',borderRadius:12,padding:'14px 28px',color:'#FF7A00',fontFamily:'Sora,sans-serif',fontWeight:800,fontSize:15,cursor:'pointer',boxShadow:'0 4px 20px rgba(0,0,0,0.15)'}}>
            <FileText size={17}/> Get Free Quote
          </button>
          <button onClick={() => navigate('contact')} style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(255,255,255,0.15)',border:'2px solid rgba(255,255,255,0.5)',borderRadius:12,padding:'14px 28px',color:'#fff',fontFamily:'Sora,sans-serif',fontWeight:700,fontSize:15,cursor:'pointer'}}>
            <MessageCircle size={17}/> Chat with Us
          </button>
        </div>
      </section>

      <Footer navigate={navigate}/>
    </div>
  );
}
